import React, { useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LocationOn,
  Directions,
  Navigation,
  Phone,
  Email,
  OpenInNew,
} from '@mui/icons-material';

import findus from '../assets/findus.png';

const InteractiveMap = () => {
  const mapRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Resort coordinates (Guhagar, Maharashtra)
  const resortLocation = {
    lat: 17.5238125,
    lng: 73.2475625,
    address: 'G6FX+G2, Palpene, Maharashtra 415724'
  };

  useEffect(() => {
    // Load Google Maps API
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        initMap();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyB41DRUbKWJHPxaFjMAwdrzWzbVKartNGg&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initMap;
      script.onerror = () => {
        console.log('Google Maps failed to load, showing fallback');
        showFallbackMap();
      };
      document.head.appendChild(script);
    };

    const initMap = () => {
      if (!mapRef.current) return;

      try {
        const map = new window.google.maps.Map(mapRef.current, {
          center: resortLocation,
          zoom: 15,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

        // Add marker for the resort
        const marker = new window.google.maps.Marker({
          position: resortLocation,
          map: map,
          title: 'Cashew County Resort',
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="18" fill="#8B0000" stroke="white" stroke-width="2"/>
                <path d="M20 8c-4.4 0-8 3.6-8 8 0 6 8 16 8 16s8-10 8-16c0-4.4-3.6-8-8-8zm0 12c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z" fill="white"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 20)
          }
        });

        // Add info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 10px; max-width: 250px;">
              <h3 style="margin: 0 0 8px 0; color: #8B0000; font-size: 16px;">Cashew County Resort</h3>
              <p style="margin: 0; font-size: 14px; color: #666;">${resortLocation.address}</p>
              <p style="margin: 8px 0 0 0; font-size: 12px; color: #888;">Coordinates: ${resortLocation.lat}, ${resortLocation.lng}</p>
            </div>
          `
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });

      } catch (error) {
        console.error('Error initializing Google Maps:', error);
        showFallbackMap();
      }
    };

    const showFallbackMap = () => {
      if (!mapRef.current) return;
      
      mapRef.current.innerHTML = `
        <div style="
          width: 100%; 
          height: 100%; 
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          color: white;
          text-align: center;
          padding: 20px;
        ">
          <img src="https://maps.gstatic.com/tactile/basepage/pegman_sherlock.png" 
               alt="Map Fallback" 
               style="width: 80px; height: 80px; margin-bottom: 20px;" />
        </div>
      `;
    };

    loadGoogleMaps();
  }, []);

  const handleGetDirections = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(resortLocation.address)}`;
    window.open(url, '_blank');
  };

  const handleStartNavigation = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${resortLocation.lat},${resortLocation.lng}&travelmode=driving`;
    window.open(url, '_blank');
  };

  const handleOpenInMaps = () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(resortLocation.address)}`;
    window.open(url, '_blank');
  };

  const handleCall = () => {
    window.open('tel:+919049527485', '_self');
  };

  const handleEmail = () => {
    window.open('mailto:cashewcounty.guhagar@gmail.com', '_self');
  };

  return (
    <Box sx={{ mt: 8 }}>
      <Typography
        variant="h3"
        component="h2"
        align="center"
        gutterBottom
        sx={{ fontWeight: 700, color: 'primary.main', mb: 4 }}
      >
        Find Us
      </Typography>

      <Grid container spacing={4}>
        {/* Map */}
        <Grid item xs={12} lg={8}>
          <Paper
            elevation={3}
            sx={{
              height: '500px',
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Box
              
              sx={{
                width: '100%',
                height: '100%',
                backgroundColor: '#f0f0f0',
              }}
            >
              <img src={findus} width={'100%'} height={'100%'} />
            </Box>
          </Paper>
        </Grid>

        {/* Navigation Options */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Card elevation={3}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  component="h3"
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}
                >
                  Get Directions
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                  Get step-by-step directions to our resort from your current location
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Directions />}
                  onClick={handleGetDirections}
                  sx={{ mb: 2 }}
                >
                  Get Directions
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Navigation />}
                  onClick={handleStartNavigation}
                >
                  Start Navigation
                </Button>
              </CardContent>
            </Card>

            <Card elevation={3}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  component="h3"
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}
                >
                  Contact Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Phone />}
                    onClick={handleCall}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Call: +919049527485
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Email />}
                    onClick={handleEmail}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Email Us : cashewcounty.guhagar@gmail.com
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card elevation={3}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  component="h3"
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}
                >
                  Address & Coordinates
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 2 }}>
                  {resortLocation.address}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                  Lat: {resortLocation.lat}<br />
                  Lng: {resortLocation.lng}
                </Typography>
              </CardContent>
            </Card>

          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InteractiveMap;